import { useEffect, useState, useCallback } from 'react';
import {
  Activity,
  RefreshCw,
  ExternalLink,
  Cpu,
  Image,
  CalendarCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { authService } from '../../services/auth';
import type { SiteStatus } from '../../services/types';

const siteIcons: Record<string, LucideIcon> = {
  witelab: Sparkles,
  ai: Cpu,
  mockup: Image,
  daily: CalendarCheck,
};

const siteGradients: Record<string, string> = {
  witelab: 'from-indigo-500 to-purple-600',
  ai: 'from-emerald-400 to-cyan-500',
  mockup: 'from-violet-500 to-indigo-600',
  daily: 'from-amber-400 to-orange-500',
};

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${Math.floor(seconds)}с`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}м`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}ч ${Math.floor((seconds % 3600) / 60)}м`;
  return `${Math.floor(seconds / 86400)}д ${Math.floor((seconds % 86400) / 3600)}ч`;
}

function timeAgo(ts: number | null): string {
  if (!ts) return 'никогда';
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'только что';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)} мин назад`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)} ч назад`;
  return `${Math.floor(diff / 86_400_000)} д назад`;
}

interface UserActivity {
  id: string;
  username: string;
  role: string;
  last_login_at: number | null;
  last_active_at: number | null;
  is_online: boolean;
}

export function DashboardPage() {
  const [sites, setSites] = useState<SiteStatus[]>([]);
  const [activity, setActivity] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSites = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [sitesRes, activityRes] = await Promise.all([
        authService.apiFetch('/api/admin/sites'),
        authService.apiFetch('/api/auth/users/activity'),
      ]);
      const sitesData = await sitesRes.json();
      if (sitesData.success) setSites(sitesData.data.sites);
      const activityData = await activityRes.json();
      if (activityData.success) setActivity(activityData.data);
    } catch { /* ignore */ }

    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    fetchSites();
    const interval = setInterval(() => fetchSites(), 30000);
    return () => clearInterval(interval);
  }, [fetchSites]);

  const onlineCount = sites.filter((s) => s.status === 'online').length;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Дашборд</h1>
          <p className="text-sm text-slate-500 mt-1">
            Статус экосистемы: {onlineCount}/{sites.length} онлайн
          </p>
        </div>
        <button
          onClick={() => fetchSites(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-200 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] transition disabled:opacity-40"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Обновить
        </button>
      </div>

      {/* Sites Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-40 rounded-xl bg-slate-800/40 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sites.map((site) => {
            const Icon = siteIcons[site.id] || Activity;
            const gradient = siteGradients[site.id] || 'from-slate-500 to-slate-600';

            return (
              <div
                key={site.id}
                className="rounded-xl border border-white/[0.06] bg-slate-800/40 p-5 hover:bg-slate-800/60 transition"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${gradient}`}
                    >
                      <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-slate-100">{site.name}</h3>
                      <p className="text-xs text-slate-500">{site.description}</p>
                    </div>
                  </div>

                  {/* Status badge */}
                  <div className="flex items-center gap-1.5">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        site.status === 'online'
                          ? 'bg-emerald-400 animate-pulse'
                          : site.status === 'error'
                            ? 'bg-amber-400'
                            : 'bg-slate-600'
                      }`}
                    />
                    <span
                      className={`text-xs font-medium ${
                        site.status === 'online'
                          ? 'text-emerald-400'
                          : site.status === 'error'
                            ? 'text-amber-400'
                            : 'text-slate-600'
                      }`}
                    >
                      {site.status === 'online'
                        ? 'онлайн'
                        : site.status === 'error'
                          ? 'ошибка'
                          : 'оффлайн'}
                    </span>
                  </div>
                </div>

                {/* Info row */}
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  {site.uptime !== null && (
                    <span>Аптайм: {formatUptime(site.uptime)}</span>
                  )}
                  {site.version && <span>v{site.version}</span>}
                  <a
                    href={site.clientUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto flex items-center gap-1 text-slate-500 hover:text-indigo-400 transition"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Открыть
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* User Activity */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-slate-400" />
          <h2 className="text-lg font-semibold text-slate-100">Пользователи</h2>
          <span className="text-xs text-slate-500 ml-2">
            {activity.filter(u => u.is_online).length} онлайн
          </span>
        </div>
        {activity.length === 0 ? (
          <div className="text-sm text-slate-600">Нет данных</div>
        ) : (
          <div className="rounded-xl border border-white/[0.06] bg-slate-800/40 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06] text-xs text-slate-500">
                  <th className="text-left px-4 py-3 font-medium">Пользователь</th>
                  <th className="text-left px-4 py-3 font-medium">Роль</th>
                  <th className="text-left px-4 py-3 font-medium">Статус</th>
                  <th className="text-left px-4 py-3 font-medium">Последний вход</th>
                  <th className="text-left px-4 py-3 font-medium">Активность</th>
                </tr>
              </thead>
              <tbody>
                {activity.map(u => (
                  <tr key={u.id} className="border-b border-white/[0.04] last:border-0">
                    <td className="px-4 py-3 text-slate-200 font-medium">{u.username}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        u.role === 'admin'
                          ? 'bg-indigo-500/20 text-indigo-400'
                          : 'bg-slate-700/40 text-slate-400'
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${
                          u.is_online ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'
                        }`} />
                        <span className={`text-xs ${
                          u.is_online ? 'text-emerald-400' : 'text-slate-500'
                        }`}>
                          {u.is_online ? 'онлайн' : 'оффлайн'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {timeAgo(u.last_login_at)}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {timeAgo(u.last_active_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
